import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Modal,
  ModalDialog,
  Typography,
  Input,
} from "@mui/joy";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import CloseIcon from "@mui/icons-material/Close";
import { useAuthFetch } from "../../../../utils/useAuthFetch";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";

interface PdfViewerProps {
  documentId: number;
  sourceDocumentId: number;
  initialPage: number | null;
  sourceFilename: string;
  onClose: () => void;
}

// Cache page images across viewer sessions to avoid re-fetching
const pageImageCache = new Map<string, string>();

const PdfViewer: React.FC<PdfViewerProps> = ({
  documentId,
  sourceDocumentId,
  initialPage,
  sourceFilename,
  onClose,
}) => {
  const authFetch = useAuthFetch("raw");
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(initialPage || 1);
  const [scale, setScale] = useState<number>(1.0);
  const [pageImageUrl, setPageImageUrl] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageInputValue, setPageInputValue] = useState<string>(
    String(initialPage || 1),
  );
  const abortRef = useRef<AbortController | null>(null);

  const basePath = `/api/workspace/meridian/documents/${documentId}/sources/${sourceDocumentId}/pdf`;

  // Fetch page count on mount
  useEffect(() => {
    let cancelled = false;
    const fetchInfo = async () => {
      try {
        const response = await authFetch(
          `${basePath}/info?workspace_id=${workspaceId}`,
          "fetch PDF info",
        );
        if (cancelled) return;
        const data = await (response as Response).json();
        setNumPages(data.num_pages);
        if (initialPage && initialPage > data.num_pages) {
          setCurrentPage(data.num_pages);
          setPageInputValue(String(data.num_pages));
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load PDF info");
        }
      }
    };
    if (workspaceId) fetchInfo();
    return () => {
      cancelled = true;
    };
  }, [
    documentId,
    sourceDocumentId,
    workspaceId,
    authFetch,
    basePath,
    initialPage,
  ]);

  // Fetch current page image (lazy -- one page at a time)
  useEffect(() => {
    if (!workspaceId || !currentPage) return;

    // Check cache first
    const cacheKey = `${documentId}-${sourceDocumentId}-${currentPage}`;
    const cached = pageImageCache.get(cacheKey);
    if (cached) {
      setPageImageUrl(cached);
      setPageLoading(false);
      return;
    }

    // Cancel previous in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let objectUrl: string | null = null;
    setPageLoading(true);

    const fetchPage = async () => {
      try {
        const response = await authFetch(
          `${basePath}/page/${currentPage}?workspace_id=${workspaceId}`,
          "fetch PDF page",
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        const blob = await (response as Response).blob();
        if (controller.signal.aborted) return;

        objectUrl = URL.createObjectURL(blob);
        pageImageCache.set(cacheKey, objectUrl);
        setPageImageUrl(objectUrl);
        setError(null);
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Failed to load page");
      } finally {
        if (!controller.signal.aborted) setPageLoading(false);
      }
    };

    fetchPage();

    return () => {
      controller.abort();
    };
  }, [
    documentId,
    sourceDocumentId,
    currentPage,
    workspaceId,
    authFetch,
    basePath,
  ]);

  // Prefetch adjacent pages
  useEffect(() => {
    if (!workspaceId || !numPages) return;

    const pagesToPrefetch = [currentPage - 1, currentPage + 1].filter(
      (p) => p >= 1 && p <= numPages,
    );

    pagesToPrefetch.forEach((page) => {
      const cacheKey = `${documentId}-${sourceDocumentId}-${page}`;
      if (pageImageCache.has(cacheKey)) return;

      authFetch(
        `${basePath}/page/${page}?workspace_id=${workspaceId}`,
        "prefetch PDF page",
      )
        .then(async (response) => {
          const blob = await (response as Response).blob();
          const url = URL.createObjectURL(blob);
          pageImageCache.set(cacheKey, url);
        })
        .catch(() => {
          // Prefetch failures are non-critical
        });
    });
  }, [
    currentPage,
    numPages,
    documentId,
    sourceDocumentId,
    workspaceId,
    authFetch,
    basePath,
  ]);

  const goToPage = useCallback(
    (page: number) => {
      const clamped = Math.max(1, Math.min(page, numPages || 1));
      setCurrentPage(clamped);
      setPageInputValue(String(clamped));
    },
    [numPages],
  );

  const handlePageInputSubmit = useCallback(() => {
    const parsed = parseInt(pageInputValue, 10);
    if (!isNaN(parsed)) {
      goToPage(parsed);
    } else {
      setPageInputValue(String(currentPage));
    }
  }, [pageInputValue, goToPage, currentPage]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        goToPage(currentPage - 1);
      } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        goToPage(currentPage + 1);
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentPage, goToPage, onClose]);

  return (
    <Modal open onClose={onClose}>
      <ModalDialog
        sx={{
          width: "90vw",
          maxWidth: 900,
          height: "90vh",
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            borderBottom: "1px solid",
            borderColor: "neutral.200",
            flexShrink: 0,
          }}
        >
          <Typography level="title-sm" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {sourceFilename}
          </Typography>

          {/* Page navigation */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="sm"
              variant="plain"
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <Input
              size="sm"
              value={pageInputValue}
              onChange={(e) => setPageInputValue(e.target.value)}
              onBlur={handlePageInputSubmit}
              onKeyDown={(e) => {
                if (e.key === "Enter") handlePageInputSubmit();
              }}
              sx={{
                width: 48,
                textAlign: "center",
                "& input": { textAlign: "center" },
              }}
            />
            <Typography
              level="body-sm"
              sx={{ color: "neutral.500", whiteSpace: "nowrap" }}
            >
              / {numPages || "..."}
            </Typography>
            <IconButton
              size="sm"
              variant="plain"
              disabled={currentPage >= numPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>

          {/* Zoom controls */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 2 }}>
            <IconButton
              size="sm"
              variant="plain"
              onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <Typography
              level="body-xs"
              sx={{ minWidth: 40, textAlign: "center" }}
            >
              {Math.round(scale * 100)}%
            </Typography>
            <IconButton
              size="sm"
              variant="plain"
              onClick={() => setScale((s) => Math.min(3, s + 0.25))}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
            <IconButton size="sm" variant="plain" onClick={() => setScale(1.0)}>
              <FitScreenIcon fontSize="small" />
            </IconButton>
            <IconButton size="sm" variant="plain" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* PDF page content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: pageLoading ? "center" : "flex-start",
            bgcolor: "neutral.100",
            py: 2,
          }}
        >
          {error && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
              }}
            >
              <Typography level="body-sm" color="danger">
                {error}
              </Typography>
            </Box>
          )}

          {pageLoading && !error && <CircularProgress size="sm" />}

          {pageImageUrl && !error && (
            <img
              src={pageImageUrl}
              alt={`Page ${currentPage}`}
              style={{
                maxWidth: "100%",
                height: "auto",
                transform: `scale(${scale})`,
                transformOrigin: "top center",
                transition: "transform 0.15s ease",
                display: pageLoading ? "none" : "block",
              }}
            />
          )}
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default React.memo(PdfViewer);
